import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function SubscriptionResultPage() {
    const [params] = useSearchParams();
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState("checking");
    const [info, setInfo] = useState(null);

    useEffect(() => {
        const sessionId = params.get("session_id");
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(`/billing/status/${sessionId}`);
                setInfo(data);
                if (data.payment_status === "paid") {
                    setStatus("success");
                    refresh();
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                if (attempts >= 6) {
                    setStatus("pending");
                    return;
                }
                setTimeout(poll, 2000);
            } catch (e) {
                setStatus("error");
            }
        };
        poll();
    }, [params, refresh]);

    return (
        <div
            className="min-h-screen flex items-center justify-center bg-sf-bg p-6"
            data-testid="subscription-result-page"
        >
            <div className="max-w-md w-full bg-white border border-sf rounded-2xl p-10 text-center">
                {status === "checking" && (
                    <>
                        <Loader2
                            size={48}
                            className="mx-auto text-amber-brand animate-spin mb-4"
                        />
                        <div className="font-display text-2xl text-navy">
                            Verifica pagamento…
                        </div>
                    </>
                )}
                {status === "success" && (
                    <>
                        <CheckCircle2
                            size={64}
                            className="mx-auto text-[#2d6a4f] mb-4"
                        />
                        <div className="label-eyebrow mb-2">Successo</div>
                        <div className="font-display text-3xl text-navy mb-3">
                            Pagamento confermato
                        </div>
                        <div className="text-sf-secondary mb-6">
                            Il tuo abbonamento è ora attivo. Buon lavoro!
                        </div>
                        <Button
                            className="bg-navy text-white"
                            onClick={() => navigate("/app")}
                            data-testid="sub-result-continue"
                        >
                            Vai alla dashboard
                        </Button>
                    </>
                )}
                {(status === "expired" || status === "error" || status === "pending") && (
                    <>
                        <XCircle size={64} className="mx-auto text-red-500 mb-4" />
                        <div className="font-display text-2xl text-navy mb-2">
                            {status === "expired"
                                ? "Sessione scaduta"
                                : status === "pending"
                                ? "Pagamento ancora in elaborazione"
                                : "Errore"}
                        </div>
                        <div className="text-sf-secondary mb-6">
                            Puoi riprovare quando vuoi dalla pagina dei prezzi.
                        </div>
                        <Link to="/pricing">
                            <Button variant="outline" data-testid="sub-result-retry">
                                Torna ai piani
                            </Button>
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
